import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { statisticsApi } from '../api/client';
import Card from '../components/Card';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'];

const Statistics = () => {
  const [period, setPeriod] = useState('1m');
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Форматируем дату для input[type="date"]
  const formatDateForInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Получаем начальную и конечную дату для кастомного периода
  const getCustomDates = () => {
    if (period === 'custom' && customStartDate && customEndDate) {
      return {
        startDate: customStartDate,
        endDate: customEndDate,
      };
    }
    return { startDate: null, endDate: null };
  };

  const { startDate: queryStartDate, endDate: queryEndDate } = getCustomDates();

  const { data: statistics, isLoading, error } = useQuery({
    queryKey: ['statistics', period, queryStartDate, queryEndDate],
    queryFn: async () => {
      const response = await statisticsApi.getStatistics(period, queryStartDate, queryEndDate);
      return response.data;
    },
    enabled: period !== 'custom' || (customStartDate && customEndDate),
  });

  const periodLabels = {
    '1m': 'Месяц',
    '3m': '3 месяца',
    '6m': '6 месяцев',
    '1y': 'Год',
    'all': 'За всё время',
    'custom': 'Выбрать период',
  };

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
    if (newPeriod !== 'custom') {
      setShowCustomDatePicker(false);
    } else {
      setShowCustomDatePicker(true);
      // Устанавливаем дефолтные даты (последний месяц)
      if (!customStartDate || !customEndDate) {
        const end = new Date();
        const start = new Date();
        start.setMonth(start.getMonth() - 1);
        setCustomStartDate(formatDateForInput(start));
        setCustomEndDate(formatDateForInput(end));
      }
    }
  };

  const handleCustomDateApply = () => {
    if (customStartDate && customEndDate) {
      if (new Date(customStartDate) > new Date(customEndDate)) {
        alert('Дата начала не может быть позже даты окончания');
        return;
      }
      setShowCustomDatePicker(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-3 sm:p-4">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">Статистика</h1>
        <div className="text-center py-8">
          <p className="text-gray-500 text-sm sm:text-base">Загрузка данных...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-3 sm:p-4">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">Статистика</h1>
        <Card className="p-3 sm:p-4 bg-red-50">
          <p className="text-red-600 text-sm sm:text-base">Ошибка загрузки статистики: {error.message}</p>
        </Card>
      </div>
    );
  }

  if (!statistics) {
    return null;
  }

  // Форматирование чисел
  const formatNumber = (num) => {
    return new Intl.NumberFormat('ru-RU', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    }).format(num);
  };

  const formatCurrency = (num) => {
    return new Intl.NumberFormat('ru-RU', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  // Подготовка данных для графика по месяцам
  const monthlyChartData = statistics.monthlyStats.map((item) => ({
    month: new Date(item.month + '-01').toLocaleDateString('ru-RU', { month: 'short', year: 'numeric' }),
    quantity: item.total_qty,
    revenue: item.total_revenue,
    orders: item.order_count,
  }));

  // Подготовка данных для графика по продуктам (топ 10)
  const topProductsChartData = statistics.topProducts.map((product, index) => ({
    name: product.name.length > 15 ? product.name.substring(0, 15) + '...' : product.name,
    fullName: product.name,
    quantity: product.total_qty,
    revenue: product.total_revenue,
    orders: product.order_count,
    color: COLORS[index % COLORS.length],
  }));

  // Подготовка данных для круговой диаграммы (топ 5)
  const pieChartData = statistics.topProducts.slice(0, 5).map((product, index) => ({
    name: product.name.length > 12 ? product.name.substring(0, 12) + '...' : product.name,
    fullName: product.name,
    value: product.total_qty,
    revenue: product.total_revenue,
    color: COLORS[index % COLORS.length],
  }));

  // Подготовка данных для статистики по диоптриям
  const formatDioptryLabel = (dioptry) => {
    if (dioptry === null || dioptry === undefined) return 'Без диоптрий';
    if (dioptry === 0) return '0';
    return `-${dioptry.toFixed(2)}`;
  };

  const dioptryChartData = statistics.dioptryStats?.map((item, index) => ({
    dioptry: item.dioptry,
    label: formatDioptryLabel(item.dioptry),
    quantity: item.total_qty,
    revenue: item.total_revenue,
    orders: item.order_count,
    color: COLORS[index % COLORS.length],
  })) || [];

  return (
    <div className="p-3 sm:p-4 pb-20 sm:pb-24">
      <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">Статистика</h1>

      {/* Выбор периода */}
      <Card className="mb-3 sm:mb-4 p-3 sm:p-4">
        <div className="flex flex-wrap gap-2">
          {Object.entries(periodLabels).map(([key, label]) => (
            <button
              key={key}
              onClick={() => handlePeriodChange(key)}
              className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium text-xs sm:text-sm transition-colors ${
                period === key
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Кастомный выбор дат */}
        {showCustomDatePicker && (
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Дата начала
                </label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Дата окончания
                </label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleCustomDateApply}
                disabled={!customStartDate || !customEndDate}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Применить
              </button>
              <button
                onClick={() => {
                  setShowCustomDatePicker(false);
                  setPeriod('1m');
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300"
              >
                Отмена
              </button>
            </div>
          </div>
        )}
      </Card>

      {/* Общая статистика */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-3 mb-3 sm:mb-4">
        <Card className="p-2 sm:p-3">
          <p className="text-xs text-gray-600 mb-0.5 sm:mb-1">Выручка</p>
          <p className="text-base sm:text-lg font-bold text-blue-600">
            {formatCurrency(statistics.totals.total_revenue)} ₸
          </p>
        </Card>
        {/* <Card className="p-2 sm:p-3">
          <p className="text-xs text-gray-600 mb-0.5 sm:mb-1">Заказов</p>
          <p className="text-base sm:text-lg font-bold text-green-600">
            {formatNumber(statistics.totals.total_orders)}
          </p>
        </Card>
        <Card className="p-2 sm:p-3">
          <p className="text-xs text-gray-600 mb-0.5 sm:mb-1">Продано</p>
          <p className="text-base sm:text-lg font-bold text-purple-600">
            {formatNumber(statistics.totals.total_items_sold)}
          </p>
        </Card> */}
        {statistics.stockInfo && (
          <>
            {/* <Card className="p-2 sm:p-3 border-2 border-orange-200 bg-orange-50">
              <p className="text-xs text-gray-600 mb-0.5 sm:mb-1">В наличии</p>
              <p className="text-base sm:text-lg font-bold text-orange-600">
                {formatNumber(statistics.stockInfo.total_stock_qty)}
              </p>
            </Card> */}
            {/* <Card className="p-2 sm:p-3 border-2 border-indigo-200 bg-indigo-50">
              <p className="text-xs text-gray-600 mb-0.5 sm:mb-1">Стоимость</p>
              <p className="text-base sm:text-lg font-bold text-indigo-600">
                {formatCurrency(statistics.stockInfo.total_stock_value)} ₸
              </p>
            </Card> */}
          </>
        )}
      </div>

      {/* График продаж по месяцам
      {monthlyChartData.length > 0 && (
        <Card className="mb-3 sm:mb-4 p-3 sm:p-4">
          <h2 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Продажи по месяцам</h2>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={monthlyChartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="month" 
                tick={{ fontSize: 10 }}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis yAxisId="left" tick={{ fontSize: 10 }} width={50} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} width={50} />
              <Tooltip
                formatter={(value, name) => {
                  if (name === 'revenue') return `${formatCurrency(value)} ₸`;
                  if (name === 'quantity') return formatNumber(value);
                  return value;
                }}
                labelFormatter={(label) => `Месяц: ${label}`}
                contentStyle={{ fontSize: '12px' }}
              />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="quantity"
                stroke="#3b82f6"
                strokeWidth={2}
                name="Количество"
                dot={{ r: 3 }}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="revenue"
                stroke="#10b981"
                strokeWidth={2}
                name="Выручка (₸)"
                dot={{ r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      )} */}

      {/* Топ 10 продаж - столбчатая диаграмма */}
      {/* {topProductsChartData.length > 0 && (
        <Card className="mb-3 sm:mb-4 p-3 sm:p-4">
          <h2 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Топ 10 продаж по количеству</h2>
          <ResponsiveContainer width="100%" height={Math.min(400, topProductsChartData.length * 35)}>
            <BarChart data={topProductsChartData} layout="vertical" margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tick={{ fontSize: 10 }} />
              <YAxis
                dataKey="name"
                type="category"
                width={120}
                tick={{ fontSize: 10 }}
              />
              <Tooltip
                formatter={(value, name) => {
                  if (name === 'revenue') return `${formatCurrency(value)} ₸`;
                  if (name === 'quantity') return formatNumber(value);
                  return value;
                }}
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    return (
                      <div className="bg-white p-2 sm:p-3 border border-gray-200 rounded shadow-lg text-xs sm:text-sm">
                        <p className="font-semibold mb-1 sm:mb-2">{data.fullName}</p>
                        <p className="text-gray-600">
                          Количество: <span className="font-bold">{formatNumber(data.quantity)}</span>
                        </p>
                        <p className="text-gray-600">
                          Выручка: <span className="font-bold">{formatCurrency(data.revenue)} ₸</span>
                        </p>
                        <p className="text-gray-600">
                          Заказов: <span className="font-bold">{data.orders}</span>
                        </p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Bar dataKey="quantity" fill="#3b82f6" name="Количество" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )} */}

      {/* Круговая диаграмма топ 5 */}
      {pieChartData.length > 0 && (
        <Card className="mb-3 sm:mb-4 p-3 sm:p-4">
          <h2 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Топ 5 продуктов (по количеству)</h2>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={pieChartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {pieChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value, name, props) => {
                  if (name === 'value') {
                    return [
                      `${formatNumber(value)} шт. (${formatCurrency(props.payload.revenue)} ₸)`,
                      'Количество',
                    ];
                  }
                  return value;
                }}
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    return (
                      <div className="bg-white p-2 sm:p-3 border border-gray-200 rounded shadow-lg text-xs sm:text-sm">
                        <p className="font-semibold mb-1 sm:mb-2">{data.fullName}</p>
                        <p className="text-gray-600">
                          Количество: <span className="font-bold">{formatNumber(data.value)}</span>
                        </p>
                        <p className="text-gray-600">
                          Выручка: <span className="font-bold">{formatCurrency(data.revenue)} ₸</span>
                        </p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Таблица топ 10 */}
      {statistics.topProducts.length > 0 && (
        <Card className="mb-3 sm:mb-4 p-3 sm:p-4">
          <h2 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Топ 10 продаж</h2>
          <div className="overflow-x-auto -mx-3 sm:mx-0">
            <div className="inline-block min-w-full align-middle">
              <table className="w-full text-xs sm:text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 px-2 font-semibold text-gray-700">#</th>
                    <th className="text-left py-2 px-2 font-semibold text-gray-700">Товар</th>
                    <th className="text-right py-2 px-2 font-semibold text-gray-700">Кол-во</th>
                    <th className="text-right py-2 px-2 font-semibold text-gray-700">Выручка</th>
                    <th className="text-right py-2 px-2 font-semibold text-gray-700">Заказов</th>
                  </tr>
                </thead>
                <tbody>
                  {statistics.topProducts.map((product, index) => (
                    <tr key={product.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-2 px-2 text-gray-600">{index + 1}</td>
                      <td className="py-2 px-2">
                        <div>
                          <p className="font-medium text-gray-900">{product.name}</p>
                          {product.item_code && (
                            <p className="text-xs text-gray-500 font-mono">{product.item_code}</p>
                          )}
                        </div>
                      </td>
                      <td className="py-2 px-2 text-right font-medium text-gray-900">
                        {formatNumber(product.total_qty)}
                      </td>
                      <td className="py-2 px-2 text-right font-medium text-blue-600">
                        {formatCurrency(product.total_revenue)} ₸
                      </td>
                      <td className="py-2 px-2 text-right text-gray-600">
                        {product.order_count}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Card>
      )}

      {statistics.topProducts.length === 0 && (
        <Card className="p-3 sm:p-4">
          <p className="text-center text-gray-500 text-sm sm:text-base">Нет данных за выбранный период</p>
        </Card>
      )}

      {/* Статистика по диоптриям */}
      {dioptryChartData.length > 0 && (
        <>
          {/* График продаж по диоптриям */}
          {/* <Card className="mb-3 sm:mb-4 p-3 sm:p-4">
            <h2 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Продажи по диоптриям</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={dioptryChartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="label" 
                  tick={{ fontSize: 10 }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis yAxisId="left" tick={{ fontSize: 10 }} width={50} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} width={50} />
                <Tooltip
                  formatter={(value, name) => {
                    if (name === 'revenue') return `${formatCurrency(value)} ₸`;
                    if (name === 'quantity') return formatNumber(value);
                    if (name === 'orders') return value;
                    return value;
                  }}
                  labelFormatter={(label) => `Диоптрии: ${label}`}
                  contentStyle={{ fontSize: '12px' }}
                />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Bar yAxisId="left" dataKey="quantity" fill="#3b82f6" name="Количество" />
                <Bar yAxisId="right" dataKey="revenue" fill="#10b981" name="Выручка (₸)" />
              </BarChart>
            </ResponsiveContainer>
          </Card> */}

          {/* Круговая диаграмма по диоптриям */}
          {/* {dioptryChartData.length > 0 && (
            <Card className="mb-3 sm:mb-4 p-3 sm:p-4">
              <h2 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Распределение по диоптриям</h2>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={dioptryChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ label, percent }) => `${label}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="quantity"
                  >
                    {dioptryChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, name, props) => {
                      if (name === 'quantity') {
                        return [
                          `${formatNumber(value)} шт. (${formatCurrency(props.payload.revenue)} ₸)`,
                          'Количество',
                        ];
                      }
                      return value;
                    }}
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-white p-2 sm:p-3 border border-gray-200 rounded shadow-lg text-xs sm:text-sm">
                            <p className="font-semibold mb-1 sm:mb-2">Диоптрии: {data.label}</p>
                            <p className="text-gray-600">
                              Количество: <span className="font-bold">{formatNumber(data.quantity)}</span>
                            </p>
                            <p className="text-gray-600">
                              Выручка: <span className="font-bold">{formatCurrency(data.revenue)} ₸</span>
                            </p>
                            <p className="text-gray-600">
                              Заказов: <span className="font-bold">{data.orders}</span>
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          )} */}

          {/* Таблица статистики по диоптриям */}
          <Card className="mb-3 sm:mb-4 p-3 sm:p-4">
            <h2 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Статистика по диоптриям</h2>
            <div className="overflow-x-auto -mx-3 sm:mx-0">
              <div className="inline-block min-w-full align-middle">
                <table className="w-full text-xs sm:text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-2 font-semibold text-gray-700">Диоптрии</th>
                      <th className="text-right py-2 px-2 font-semibold text-gray-700">Кол-во</th>
                      <th className="text-right py-2 px-2 font-semibold text-gray-700">Выручка</th>
                      <th className="text-right py-2 px-2 font-semibold text-gray-700">Заказов</th>
                      <th className="text-right py-2 px-2 font-semibold text-gray-700">% от общего</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dioptryChartData.map((item, index) => {
                      const totalQty = statistics.totals.total_items_sold || 1;
                      const percentage = ((item.quantity / totalQty) * 100).toFixed(1);
                      return (
                        <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-2 px-2">
                            <div className="flex items-center gap-2">
                              <div
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: item.color }}
                              />
                              <span className="font-medium text-gray-900">{item.label}</span>
                            </div>
                          </td>
                          <td className="py-2 px-2 text-right font-medium text-gray-900">
                            {formatNumber(item.quantity)}
                          </td>
                          <td className="py-2 px-2 text-right font-medium text-blue-600">
                            {formatCurrency(item.revenue)} ₸
                          </td>
                          <td className="py-2 px-2 text-right text-gray-600">
                            {item.orders}
                          </td>
                          <td className="py-2 px-2 text-right text-gray-600">
                            {percentage}%
                          </td>
                        </tr>
                      );
                    })}
                    {/* Итоговая строка */}
                    {(() => {
                      const totalQuantity = dioptryChartData.reduce((sum, item) => sum + item.quantity, 0);
                      const totalRevenue = dioptryChartData.reduce((sum, item) => sum + item.revenue, 0);
                      const totalOrders = dioptryChartData.reduce((sum, item) => sum + item.orders, 0);
                      return (
                        <tr className="border-t-2 border-gray-300 bg-gray-50 font-semibold">
                          <td className="py-3 px-2">
                            <span className="text-gray-900">Итого</span>
                          </td>
                          <td className="py-3 px-2 text-right text-gray-900">
                            {formatNumber(totalQuantity)}
                          </td>
                          <td className="py-3 px-2 text-right text-blue-600">
                            {formatCurrency(totalRevenue)} ₸
                          </td>
                          <td className="py-3 px-2 text-right text-gray-900">
                            {totalOrders}
                          </td>
                          <td className="py-3 px-2 text-right text-gray-900">
                            100%
                          </td>
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

export default Statistics;
